import {ImportModuleFormPlacement} from '../../../core/definitions/import-module';

/** The `/target_fields` block for fields and card kinds in no section of
 * the form. */
export const OTHER_FIELDS_SECTION_KEY = 'other_fields';
const OTHER_FIELDS_TITLE_KEY = 'TRANS__IMPORT__TARGET_SECTION__OTHER_FIELDS';

/** The list's own block for the rows with no target field picked yet. */
export const NO_FIELD_SECTION_KEY = 'no_field';
const NO_FIELD_TITLE_KEY = 'TRANS__IMPORT__MAPPING__NO_FIELD_SECTION';

/** The list's block for the rows onto a field of the linked media, after
 * every section of the form. */
export const MEDIA_FIELDS_PLACEMENT: ImportModuleFormPlacement = {
  section_key: 'media_fields', section_title: 'TRANS__IMPORT__MAPPING__MEDIA_FIELDS_SECTION',
  section_order: Number.MAX_SAFE_INTEGER, field_order: null,
};

/** One heading of the mapping list and the lines under it. */
export interface TargetSection<L> {
  key: string;
  titleKey: string;
  lines: L[];
}

/** A line and where it sits. A placement with no `section_key` (a
 * client-side target, or a card kind `/target_fields` did not list) puts
 * the line under the other fields; `null` puts it under the rows with no
 * field. */
export interface PlacedLine<L> {
  line: L;
  placement: Partial<ImportModuleFormPlacement> | null;
}

/** Where a row onto `field` sits: a client-side target by its
 * `specialTargets` entry, any other field by its own placement. A
 * client-side target with no entry has no section, which puts it under
 * the other fields. */
export function fieldPlacement(
  field: Partial<ImportModuleFormPlacement> & { field_id: string },
  specialTargets: { readonly [target: string]: ImportModuleFormPlacement },
): Partial<ImportModuleFormPlacement> {
  return specialTargets[field.field_id] ?? field;
}

/**
 * The placement for a line in no section of the form: the `other_fields`
 * block as `/target_fields` returned it on any of `placements`, else that
 * block after every section they name.
 */
export function otherFieldsPlacement(
  placements: readonly Partial<ImportModuleFormPlacement>[],
): ImportModuleFormPlacement {
  const returned = placements.find(placement => placement.section_key === OTHER_FIELDS_SECTION_KEY);
  if (returned?.section_title && returned.section_order !== undefined) {
    return {
      section_key: OTHER_FIELDS_SECTION_KEY, section_title: returned.section_title,
      section_order: returned.section_order, field_order: null,
    };
  }
  const lastOrder = Math.max(0, ...placements.map(placement => placement.section_order ?? 0));
  return {
    section_key: OTHER_FIELDS_SECTION_KEY, section_title: OTHER_FIELDS_TITLE_KEY,
    section_order: lastOrder + 1, field_order: null,
  };
}

/**
 * `lines` under their section headings: the rows with no field first,
 * then the sections in `section_order`, each section's lines in
 * `field_order` (none last) and otherwise in the order given. A section
 * with no line is left out.
 */
export function groupBySection<L>(
  lines: readonly PlacedLine<L>[], otherFields: ImportModuleFormPlacement,
): TargetSection<L>[] {
  const noField: L[] = [];
  const sections = new Map<string, {placement: ImportModuleFormPlacement; lines: PlacedLine<L>[]}>();
  for (const placed of lines) {
    if (!placed.placement) {
      noField.push(placed.line);
      continue;
    }
    const placement = placed.placement.section_key
      ? placed.placement as ImportModuleFormPlacement
      : otherFields;
    let section = sections.get(placement.section_key);
    if (!section) {
      section = {placement, lines: []};
      sections.set(placement.section_key, section);
    }
    section.lines.push({line: placed.line, placement});
  }
  const ordered: TargetSection<L>[] = [...sections.values()]
    .sort((a, b) => a.placement.section_order - b.placement.section_order)
    .map(section => ({
      key: section.placement.section_key,
      titleKey: section.placement.section_title,
      lines: section.lines.sort(byFieldOrder).map(placed => placed.line),
    }));
  return noField.length
    ? [{key: NO_FIELD_SECTION_KEY, titleKey: NO_FIELD_TITLE_KEY, lines: noField}, ...ordered]
    : ordered;
}

/** Ascending `field_order`, a line with none after every line with one. */
function byFieldOrder(a: PlacedLine<unknown>, b: PlacedLine<unknown>): number {
  const first = a.placement?.field_order ?? null;
  const second = b.placement?.field_order ?? null;
  if (first === second) {
    return 0;
  }
  if (first === null) {
    return 1;
  }
  return second === null ? -1 : first - second;
}
